import { useEffect, useCallback } from "react";
import { useURLState } from "./useURLState";
import { CREATIVE_ROUTE_CONFIG } from "../config/routeConfig";
import { validateRouteParams, getDefaultSubtab } from "../utils/navigationUtils";

/**
 * Simplified route validation hook for Creative system
 * Auto-fixes invalid routes with smart fallbacks
 */
export const useRouteValidation = (options = {}) => {
  const {
    autoRedirect = true,
    onInvalidRoute = null
  } = options;

  const { urlState, updateURL } = useURLState();

  // Simple validation function
  const isRouteValid = useCallback(() => {
    const { tab, subtab, pageNumber } = urlState;
    const validation = validateRouteParams(tab, subtab, pageNumber);
    return validation.isValid;
  }, [urlState]);

  // Get validation errors (simplified)
  const getValidationErrors = useCallback(() => {
    const { tab, subtab, pageNumber } = urlState;
    const validation = validateRouteParams(tab, subtab, pageNumber);
    const errors = [];

    if (!validation.isValidTab) {
      errors.push({ parameter: 'tab', value: tab });
    }
    if (!validation.isValidSubtab) {
      errors.push({ parameter: 'subtab', value: subtab });
    }
    if (!validation.isValidPage) {
      errors.push({ parameter: 'pageNumber', value: pageNumber });
    }

    return errors;
  }, [urlState]);

  // Smart route fixing - tries to fix before full redirect
  const fixInvalidRoute = useCallback(() => {
    const { tab, subtab, pageNumber } = urlState;

    // If tab is valid but subtab isn't, fix subtab
    if (CREATIVE_ROUTE_CONFIG.validTabs.includes(tab)) {
      const validSubtabs = CREATIVE_ROUTE_CONFIG.validSubtabs[tab];
      if (!validSubtabs?.includes(subtab)) {
        const fixedSubtab = getDefaultSubtab(tab);
        updateURL({ tab, subtab: fixedSubtab, page: Math.max(1, pageNumber) });
        return;
      }
    }

    // If only page number is invalid, fix it
    if (CREATIVE_ROUTE_CONFIG.validTabs.includes(tab) &&
        CREATIVE_ROUTE_CONFIG.validSubtabs[tab]?.includes(subtab) &&
        pageNumber < 1) {
      updateURL({ tab, subtab, page: 1 });
      return;
    }

    // Full redirect to default
    if (autoRedirect) {
      const { tab: defaultTab, subtab: defaultSubtab, page: defaultPage } = CREATIVE_ROUTE_CONFIG.defaultRoute;
      updateURL({ tab: defaultTab, subtab: defaultSubtab, page: defaultPage });

      if (onInvalidRoute) {
        onInvalidRoute(getValidationErrors(), urlState);
      }
    }
  }, [urlState, updateURL, autoRedirect, onInvalidRoute, getValidationErrors]);

  // Auto-fix invalid routes on mount and parameter changes
  useEffect(() => {
    if (!isRouteValid()) {
      fixInvalidRoute();
    }
  }, [isRouteValid, fixInvalidRoute]);

  // Manual validation trigger (simplified)
  const validateRoute = useCallback(() => {
    const valid = isRouteValid();
    if (!valid) {
      fixInvalidRoute();
    }
    return valid;
  }, [isRouteValid, fixInvalidRoute]);

  // Check if specific parameter combination is valid
  const isParameterCombinationValid = useCallback((tab, subtab, pageNumber = 1) => {
    const validation = validateRouteParams(tab, subtab, pageNumber);
    return validation.isValid;
  }, []);

  return {
    // Validation functions
    isRouteValid,
    validateRoute,
    getValidationErrors,
    isParameterCombinationValid,

    // Current state
    currentState: urlState
  };
};

export default useRouteValidation;